import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { tokens } from '../../theme/tokens';
import type { MappingSection } from './mappingSections';
import { GroupLabel, StepCircle, visuallyHiddenSx } from './v2/v2Ui';
import { useAppTranslation } from '../../i18n/useAppTranslation';

export interface MappingMenuItem {
  id: MappingSection;
  label: string;
  /** The live one-line summary under the label ("3 rules", "2 to confirm"), so the menu doubles as the overview. */
  status?: string;
  /** `warn` needs the store (caution text and dot); `info` is a suggestion waiting (accent text and dot). */
  tone?: 'warn' | 'info';
  /** The decision step's number. Items without one keep the empty slot so every label lines up. */
  step?: number;
  /** The last step (Unmatched Product Rules) is the amber one. */
  stepTone?: 'info' | 'caution';
}

export interface MappingMenuGroup {
  id: string;
  label: string;
  items: MappingMenuItem[];
}

interface MappingSectionLayoutProps {
  groups: MappingMenuGroup[];
  active: MappingSection;
  onSelect: (section: MappingSection) => void;
  'aria-label': string;
  children: React.ReactNode;
}

/** Below this row width (the row's own, not the viewport's) the menu becomes a scrolling strip above the content. */
export const NARROW_ROW_WIDTH = 700;

/** The row's measured width; null until measured, or where the browser cannot measure (tests), which reads as wide. */
function useRowWidth(ref: React.RefObject<HTMLElement | null>): number | null {
  const [width, setWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const initial = element.getBoundingClientRect().width;
    if (initial > 0) setWidth(initial);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next && next > 0) setWidth(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

const MenuButton: React.FC<{ item: MappingMenuItem; active: boolean; narrow: boolean; onSelect: () => void }> = ({
  item,
  active,
  narrow,
  onSelect,
}) => {
  const { t } = useAppTranslation();
  const statusColor = item.tone === 'warn'
    ? tokens.signal.caution.ink
    : item.tone === 'info' ? tokens.accent.main : tokens.ink.muted;
  const dotColor = item.tone === 'warn'
    ? tokens.signal.caution.solid
    : item.tone === 'info' ? tokens.accent.main : 'transparent';
  return (
    <ButtonBase
      onClick={onSelect}
      aria-current={active ? 'page' : undefined}
      data-testid={`mapping-menu-${item.id}`}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: 1.25,
        px: 1.25,
        py: 1,
        borderRadius: '9px',
        textAlign: 'left',
        width: narrow ? 'auto' : '100%',
        flex: narrow ? '0 0 auto' : undefined,
        whiteSpace: narrow ? 'nowrap' : 'normal',
        bgcolor: active ? tokens.surface.card : 'transparent',
        boxShadow: active ? tokens.shadow.card : 'none',
        '&:hover': { bgcolor: active ? tokens.surface.card : tokens.line.default },
        '&.Mui-focusVisible': { outline: '2px solid', outlineColor: tokens.accent.border },
      }}
    >
      {item.step !== undefined ? (
        <StepCircle n={item.step} tone={item.stepTone ?? 'info'} size={20} />
      ) : (
        <Box component="span" aria-hidden sx={{ flex: '0 0 auto', width: 20 }} />
      )}
      <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
        <Typography component="span" sx={{ fontSize: 13, fontWeight: active ? 700 : 600, color: tokens.ink.primary, lineHeight: 1.35 }}>
          {item.label}
        </Typography>
        {item.status && (
          <Typography
            component="span"
            data-testid={`mapping-menu-status-${item.id}`}
            sx={{ fontSize: 12, color: statusColor, lineHeight: 1.35, fontVariantNumeric: 'tabular-nums' }}
          >
            {item.status}
          </Typography>
        )}
        {/* The dot says it by colour; this says it in words. */}
        {item.tone && (
          <Box component="span" sx={visuallyHiddenSx}>
            {t(`integrations.shopifyMapping.menu.tone.${item.tone}`)}
          </Box>
        )}
      </Box>
      <Box
        component="span"
        aria-hidden
        data-tone={item.tone ?? 'none'}
        sx={{ flex: '0 0 auto', width: 6, height: 6, mt: '7px', borderRadius: '50%', bgcolor: dotColor }}
      />
    </ButtonBase>
  );
};

/**
 * The Mapping tab's frame: a 220px sticky section menu in groups, each item with a live status line,
 * beside the one open panel. When the row is narrower than 700px the menu becomes a horizontal
 * scrolling strip above the panel, without group labels and not sticky.
 */
const MappingSectionLayout: React.FC<MappingSectionLayoutProps> = ({ groups, active, onSelect, children, ...rest }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const width = useRowWidth(rowRef);
  const narrow = width !== null && width < NARROW_ROW_WIDTH;

  return (
    <Box
      ref={rowRef}
      data-testid="mapping-section-layout"
      data-layout={narrow ? 'narrow' : 'wide'}
      sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}
    >
      <Box
        component="nav"
        aria-label={rest['aria-label']}
        sx={narrow
          ? { flex: '1 1 100%', minWidth: 0, display: 'flex', flexDirection: 'row', gap: '4px', overflowX: 'auto', pb: 0.5 }
          : { flex: '0 0 220px', maxWidth: 220, position: 'sticky', top: 16, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {groups.map((group) => (
          <Box
            key={group.id}
            role="group"
            aria-label={group.label}
            sx={narrow
              ? { display: 'flex', flexDirection: 'row', gap: '4px', flex: '0 0 auto' }
              : { display: 'flex', flexDirection: 'column', gap: '2px' }}
          >
            {!narrow && (
              <Box sx={{ px: 1.25, pb: 0.75 }}>
                <GroupLabel>{group.label}</GroupLabel>
              </Box>
            )}
            {group.items.map((item) => (
              <MenuButton key={item.id} item={item} active={item.id === active} narrow={narrow} onSelect={() => onSelect(item.id)} />
            ))}
          </Box>
        ))}
      </Box>
      <Box sx={{ flex: '999 1 460px', minWidth: 0 }}>{children}</Box>
    </Box>
  );
};

export default MappingSectionLayout;
